<template>
  <v-content class="guest organization page" style="margin-top: -64px">
    <section v-if="organization">
      <v-img
        :src="imageUrl ? imageUrl : ''"
        :height="$vuetify.breakpoint.lgAndUp ? '500px' : '400px'"
        class="overlay overlay--navbar"
      >
      </v-img>
    </section>

    <v-container v-if="organization" class="pt-0" style="max-width: 900px">
      <v-card class="card--overlap">
        <v-layout column>
          <v-flex d-flex class="text-xs-center card--overlap__logo">
            <v-avatar size="164" :color="themes.candidate.light.muted">
              <v-avatar size="162">
                <img :src="logoUrl ? logoUrl : ''" alt="" />
              </v-avatar>
            </v-avatar>
          </v-flex>

          <v-flex d-flex class="text-xs-center mt-5">
            <v-card-text>
              <div>
                <div class="headline">{{ organization.name }}</div>
                <div class="pt-1">
                  <v-chip
                    outline
                    v-for="industry in organization.industries"
                    :key="industry"
                  >
                    <v-icon>mdi-chart-bubble</v-icon>
                    {{ industry }} Industry
                  </v-chip>
                </div>
              </div>
            </v-card-text>
          </v-flex>

          <v-flex d-flex class="text-xs-center">
            <!-- Quick overview -->
            <v-card-text>
              <v-layout row wrap align-center justify-space-around>
                <div v-if="organization.headquarters" class="px-1">
                  <v-chip outline class="transparent">
                    <v-icon left class="text-vertical-middle"
                      >mdi-map-marker</v-icon
                    >
                    {{ organization.headquarters.address }}
                  </v-chip>
                </div>
                <div v-if="organization.size" class="px-1">
                  <v-chip outline class="transparent">
                    <v-icon left class="text-vertical-middle">
                      mdi-account-group
                    </v-icon>
                    {{ organization.size }} employees
                  </v-chip>
                </div>
                <div v-if="organization.website" class="px-1">
                  <v-chip outline class="transparent">
                    <v-icon left class="text-vertical-middle"> mdi-web </v-icon>
                    <a
                      :href="organization.website"
                      target="_blank"
                      style="color: unset"
                    >
                      {{ organization.website }}
                    </a>
                  </v-chip>
                </div>
              </v-layout>
            </v-card-text>
          </v-flex>
          <v-flex v-if="organization.listing">
            <v-card-text class="text-xs-center">
              <v-btn
                small
                flat
                :class="themes.candidate.light.secondaryActionButton"
                :href="
                  `https://listings.leap.ly/listings/${organization.listing}/respond`
                "
                target="_blank"
              >
                Get updates from {{ organization.name }}
              </v-btn>
            </v-card-text>
          </v-flex>

          <v-flex d-flex class="text-xs-center">
            <!-- Social Media -->
            <v-card-text>
              <v-btn
                fab
                dark
                small
                v-if="organization.facebook"
                class="v-btn--xs mx-3"
                :color="themes.candidate.light.secondaryActionButton"
                :href="organization.facebook"
                target="_blank"
              >
                <img src="/img/facebook_logo.svg" width="20" height="20" />
              </v-btn>
              <v-btn
                fab
                dark
                small
                v-if="organization.twitter"
                class="v-btn--xs mx-3"
                :color="themes.candidate.light.secondaryActionButton"
                :href="organization.twitter"
                target="_blank"
              >
                <img src="/img/x_logo.svg" width="14" height="14" />
              </v-btn>
              <v-btn
                fab
                dark
                small
                v-if="organization.linkedin"
                class="v-btn--xs mx-3"
                :color="themes.candidate.light.secondaryActionButton"
                :href="organization.linkedin"
                target="_blank"
              >
                <v-icon>mdi-linkedin</v-icon>
              </v-btn>
              <v-btn
                fab
                dark
                small
                v-if="organization.instagram"
                class="v-btn--xs mx-3"
                :color="themes.candidate.light.secondaryActionButton"
                :href="organization.instagram"
                target="_blank"
              >
                <v-icon>mdi-instagram</v-icon>
              </v-btn>
            </v-card-text>
          </v-flex>
        </v-layout>
      </v-card>

      <!-- Summary -->
      <v-card
        class="pa-4 mt-4"
        v-if="organization.summary || organization.video"
      >
        <v-card-title class="title">
          About {{ organization.name }}
        </v-card-title>
        <v-card-text>
          <markdown
            :html="false"
            :source="organization.summary"
            class="markdown"
          />
        </v-card-text>
        <v-card-text v-if="organization.video">
          <v-img class="pb-3">
            <v-layout justify-center>
              <iframe
                :width="$vuetify.breakpoint.mdAndUp ? 560 : 280"
                :height="$vuetify.breakpoint.mdAndUp ? 315 : 158"
                :src="organization.video"
                allowfullscreen
                frameborder="0"
                allow="autoplay; encrypted-media"
              />
            </v-layout>
          </v-img>
        </v-card-text>
      </v-card>

      <!-- Culture -->
      <v-card class="pa-4 mt-4" v-if="organization.culture">
        <v-card-title class="title"
          >Working at {{ organization.name }}</v-card-title
        >
        <v-card-text>
          <markdown
            :html="false"
            :source="organization.culture || ''"
            class="markdown"
          />
        </v-card-text>
      </v-card>

      <v-card
        class="pt-4 mt-4"
        v-if="organization.locations && organization.locations.length"
      >
        <v-card-title class="title"
          >{{ organization.name }} Offices</v-card-title
        >
        <v-img height="400px">
          <leaply-map
            :markers="organization.locations"
            width="100%"
            height="400px"
          />
        </v-img>
      </v-card>

      <!-- Opportunities -->
      <v-card class="py-4 mt-4" v-if="opportunities.length > 0">
        <v-card-title class="px-4 title">
          Opportunities at {{ organization.name }}
        </v-card-title>
        <v-list two-line class="pb-0 opportunity__list">
          <v-list-tile
            v-for="opportunity in opportunities"
            :key="`opportunity-${opportunity.id}`"
            @click="open(opportunity)"
          >
            <v-list-tile-avatar>
              <v-icon
                v-if="
                  new Date(opportunity.opensAt) < new Date() &&
                    new Date(opportunity.closesAt) > new Date()
                "
                :color="themes.candidate.light.success"
              >
                mdi-circle
              </v-icon>
              <v-icon v-else :color="themes.candidate.light.muted">
                mdi-circle
              </v-icon>
            </v-list-tile-avatar>
            <v-list-tile-content>
              <v-list-tile-title> {{ opportunity.name }} </v-list-tile-title>
              <v-list-tile-sub-title
                v-if="
                  new Date(opportunity.opensAt) < new Date() &&
                    new Date(opportunity.closesAt) > new Date()
                "
              >
                {{ new Date(opportunity.closesAt) | inWords }} left to
                {{ opportunity.type == "event" ? "RSVP" : "apply" }} ({{
                  new Date(opportunity.closesAt) | date("MMMM do yyy")
                }})
              </v-list-tile-sub-title>
              <v-list-tile-sub-title
                v-else-if="new Date(opportunity.opensAt) > new Date()"
              >
                This opportunity opens
                {{ new Date(opportunity.opensAt) | date("MMMM do yyy") }}
              </v-list-tile-sub-title>
              <v-list-tile-sub-title v-else>
                This opportunity closed
                {{ new Date(opportunity.closesAt) | date("MMMM do yyy") }}
              </v-list-tile-sub-title>
            </v-list-tile-content>

            <v-list-tile-action>
              <v-btn
                v-if="!loggedIn"
                :color="themes.candidate.light.successButton"
                @click.stop="$emit('signup')"
                class="px-4"
                small
              >
                {{ opportunity.type == "event" ? "RSVP Now" : "Apply Now" }}
              </v-btn>

              <v-btn
                v-else-if="canApply(opportunity)"
                color="success"
                @click.stop="toggleDialog(opportunity)"
                block
              >
                <span class="font-weight-light">
                  {{ opportunity.type == "event" ? "RSVP" : "Apply" }}
                </span>
              </v-btn>
              <v-btn
                v-else
                @click.stop="newTab(`/opportunities/${opportunity.id}`)"
                :color="themes.candidate.light.successButton"
                block
                flat
              >
                <span class="font-weight-light">View</span>
              </v-btn>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>
        <ApplicationFormDialog
          v-model="showDialog"
          :opportunity="applicableOpportunity || applyingFor"
        />
      </v-card>

      <!-- Gallery -->
      <v-container grid-list-md fluid class="px-0" v-if="gallery.length">
        <v-layout row wrap>
          <v-flex xs6 d-flex v-for="(image, i) in gallery" :key="`media-${i}`">
            <v-card
              v-if="
                image.preview &&
                  image.hasOwnProperty('url') &&
                  image.hasOwnProperty('title')
              "
            >
              <v-img
                :src="image.preview"
                :aspect-ratio="16 / 9"
                :lazy-src="image.preview"
              >
                <v-layout
                  slot="placeholder"
                  fill-height
                  align-center
                  justify-center
                  ma-0
                >
                  <v-progress-circular
                    indeterminate
                    :color="themes.candidate.light.loading"
                  />
                </v-layout>
              </v-img>
              <v-card-text v-if="image.title && image.title.length > 0">
                {{ image.title }}
                <a
                  :href="image.url"
                  target="_blank"
                  :class="themes.candidate.light.link"
                >
                  read more...
                </a>
              </v-card-text>
            </v-card>

            <v-card v-if="image.url && image.hasOwnProperty('caption')">
              <v-img
                :src="image.url"
                :aspect-ratio="16 / 9"
                :lazy-src="image.url"
              >
                <v-layout
                  slot="placeholder"
                  fill-height
                  align-center
                  justify-center
                  ma-0
                >
                  <v-progress-circular
                    indeterminate
                    :color="themes.candidate.light.loading"
                  />
                </v-layout>
              </v-img>
              <v-card-text v-if="image.caption && image.caption.length > 0">
                {{ image.caption }}
              </v-card-text>
              <v-card-text v-else></v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </v-container>
  </v-content>
</template>

<script>
// Components
import ApplicationFormDialog from "@/components/candidate/applications/ApplicationFormDialog";
import Markdown from "vue-markdown";

// Mixins
import { OpportunityDates } from "@/mixins/OpportunityDates.js";
import { managedDocumentAttributes } from "@/mixins/managedDocumentAttributes.js";

// Helpers
import _ from "lodash";
import { mapGetters } from "vuex";

// Vue Component
export default {
  mixins: [OpportunityDates, managedDocumentAttributes],

  components: { Markdown, ApplicationFormDialog },

  created() {
    this.$store
      .dispatch("organizations/fetch", Vue.$route.params.id)
      .then(this.setDocumentAttributes)
      .then(() => this.$store.dispatch("candidate/applications/fetchAll"))
      .then(() => this.opportunities.forEach(this.checkEligibility))
      .finally(() => {
        // if (this.imageUrl) this.$store.dispatch('images/fetch', this.imageUrl)
        // if (this.logoUrl) this.$store.dispatch('images/fetch', this.logoUrl)
      });

    const signup = this.$route.query["s"] !== "0";
    if (signup && this.campaign && !this.loggedIn) this.$emit("signup");
  },

  data: () => ({ showDialog: false, applyingFor: {} }),

  computed: {
    ...mapGetters({
      applications: "candidate/applications/all",
      campaign: "campaign/model",
      fullName: "auth/fullName",
      loggedIn: "auth/loggedIn",
      role: "auth/role"
    }),

    organization() {
      return Vue.$store.getters["organizations/find"](Vue.$route.params.id);
    },

    applicableOpportunity() {
      if (!(this.showDialog && this.applyingFor)) return;

      return Vue.$store.getters["opportunities/find"](this.applyingFor.id);
    },

    imageUrl() {
      return _.get(this, "organization.coverImage");
    },

    image() {
      return this.$store.state.images.images[this.imageUrl];
    },

    logoUrl() {
      return _.get(this, "organization.logo");
    },

    logo() {
      return this.$store.state.images.images[this.logoUrl];
    },

    opportunities() {
      if (!_.get(this, "organization.opportunities.length")) return [];

      const opportunities = JSON.parse(
        JSON.stringify(this.organization.opportunities)
      );

      return opportunities.sort((a, b) => {
        const a1 = this.isOpen(a);
        const a2 = a.closesAt;
        const b1 = this.isOpen(b);
        const b2 = b.closesAt;

        if (a1 < b1) return 1;
        if (a1 > b1) return -1;
        if (!a2) return 1;
        if (!b2) return -1;
        if (a2 > b2) return 1;
        if (a2 < b2) return -1;
        return 0;
      });
    },

    gallery() {
      if (this.organization.photos && this.organization.pressLinks) {
        return this.organization.pressLinks.concat(this.organization.photos);
      }
      if (this.organization.photos) {
        return this.organization.photos;
      }
      if (this.organization.pressLinks) {
        return this.organization.pressLinks;
      }

      return [];
    }
  },

  methods: {
    newTab(uri) {
      if (!uri) return;

      window.open(uri, "_blank");
    },
    toggleDialog(opportunity) {
      this.applyingFor = opportunity;
      this.showDialog = opportunity && !this.showDialog;

      if (!_.get(this, "applicableOpportunity.id") && opportunity) {
        this.$store.dispatch("opportunities/fetch", opportunity.id);
      }
    },
    canApply(opportunity) {
      return this.getIsOpportunityOpen(opportunity);
    },
    applicationFor(opportunity) {
      return this.applications.filter(
        app => opportunity.id === app.opportunity.id
      )[0];
    },
    checkEligibility(opportunity) {
      if (!this.loggedIn) return;

      if (!this.organization || !_.get(this, "organization.opportunities")) {
        setTimeout(this.checkEligibility(opportunity), 100);
        return;
      }

      this.$store
        .dispatch("candidate/opportunities/checkEligibility", opportunity)
        .then(({ data }) => {
          opportunity = Object.assign(opportunity, { eligibility: data });
          const find = this.organization.opportunities.find(
            o => o.id == opportunity.id
          );
          if (find) {
            this.organization.opportunities.splice(
              this.organization.opportunities.indexOf(find),
              1,
              opportunity
            );
          }
        });
    },

    open(opportunity) {
      const signup = this.$route.query["s"];
      return this.$router.push(
        `/opportunities/${opportunity.id}${signup ? `?s=${signup}` : ""}`
      );
    },

    isOpen(opportunity) {
      const opened = new Date(opportunity.opensAt) < new Date();
      const closed = new Date(opportunity.closesAt) < new Date();
      return opened && !closed;
    },

    setDocumentAttributes() {
      if (_.get(this, "organization.name")) {
        this.documentAttributes.title = `${this.organization.name} – Leaply`;
        this.documentAttributes.description = this.organization.blurb;
        this.documentAttributes.image = this.logoUrl;
      } else {
        setTimeout(this.setDocumentAttributes, 100);
      }
    }
  }
};
</script>

<style lang="scss">
.guest.organization.page .opportunity.card .list__tile {
  padding-left: 0;
  padding-right: 0;
}

.guest.organization.page .parallax .parallax__image-container {
  opacity: 0.6;
}

.guest.organization.page {
  .overlay::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.2);
  }

  .overlay.overlay--navbar::after {
    height: 0;
  }

  @media (min-width: 960px) {
    .overlay.overlay--navbar::after {
      height: 64px;
    }
  }

  .card--overlap {
    margin-top: -3.9rem;
  }

  .card--overlap__logo {
    position: absolute;
    transform: translate(-50%, -70%);
    left: 50%;
  }

  .v-btn--xs,
  .v-btn--xs::before {
    min-width: 0;
    width: 32px;
    height: 32px;
  }

  .opportunity__list > div:nth-child(2n) {
    background-color: #f4f4f4;
  }
}
</style>
